import { Navigate, NavLink, useParams } from "react-router";
import { ACTIVITY_KIND_LABEL, LOCATION_LABEL, STAGE_LABEL, fmt } from "@h2lca/core";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { InfoTip } from "@/components/ui/InfoTip";
import { DENOMINATOR_LABEL } from "@/components/workshop/helpers";
import { useMaster } from "@/store/master";

const KINDS = [
  { id: "factors", label: "배출계수" },
  { id: "sources", label: "전력원" },
  { id: "technologies", label: "대상기술" },
  { id: "templates", label: "설비" },
  { id: "methods", label: "산정방식" },
  { id: "glossary", label: "용어" },
] as const;
type KindId = (typeof KINDS)[number]["id"];

/**
 * 참고 데이터 , 계산에 쓰이는 값과 근거를 확인한다(값,단위,출처,기준연도,적용 범위).
 * 편집은 관리자 콘솔에서 한다. 여기는 읽기 전용.
 */
export function DataPage() {
  const { kind } = useParams();
  const origin = useMaster((s) => s.origin);
  const loadedAt = useMaster((s) => s.loadedAt);
  const active = KINDS.find((k) => k.id === kind)?.id;
  if (!active) return <Navigate to="/data/factors" replace />;

  return (
    <AppShell>
      <div className="page">
        <PageHeader
          title="참고 데이터"
          description={`산정에 사용하는 값과 출처, 기준연도, 적용 범위를 확인할 수 있습니다.${origin === "server" && loadedAt ? ` 조회 시각: ${new Date(loadedAt).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}.` : " 프로그램에 내장된 기본값입니다."}`}
        />
        <nav className="seg data-tabs" aria-label="데이터 종류">
          {KINDS.map((k) => (
            <NavLink key={k.id} to={`/data/${k.id}`} className={({ isActive }) => `seg-item${isActive ? " seg-item-on" : ""}`}>
              {k.label}
            </NavLink>
          ))}
        </nav>
        <Table kind={active} />
      </div>
    </AppShell>
  );
}

function Table({ kind }: { kind: KindId }) {
  const data = useMaster((s) => s.data);
  switch (kind) {
    case "factors":
      return (
        <div className="table-scroll data-table-wrap">
          <table className={`data-table data-table-${kind}`}>
            <thead>
              <tr>
                <th>이름</th>
                <th>적용 항목</th>
                <th className="num">값</th>
                <th>단위</th>
                <th>출처</th>
                <th className="num">기준연도</th>
                <th>적용</th>
              </tr>
            </thead>
            <tbody>
              {data.factors.filter((f) => f.active).map((f) => (
                <tr key={f.id}>
                  <td>
                    <span className="font-semibold">{f.name}</span>
                    {f.note && <InfoTip label={`${f.name} 비고`}>{f.note}</InfoTip>}
                    <span className="block font-mono text-footnote text-ink-subtle">{f.id}</span>
                  </td>
                  <td>{ACTIVITY_KIND_LABEL[f.kind]}</td>
                  <td className="num">{fmt(f.value, 4)}</td>
                  <td className="font-mono">{f.unit}</td>
                  <td className="text-caption text-ink-muted">{f.source}</td>
                  <td className="num">{f.baseYear}</td>
                  <td>{f.scope === "both" ? "공통" : f.scope === "cert" ? "인증제" : "일반 LCA"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "sources":
      return (
        <div className="table-scroll data-table-wrap">
          <table className={`data-table data-table-${kind}`}>
            <thead>
              <tr>
                <th>전력원</th>
                <th>배출계수</th>
                <th className="num">값 (kgCO2eq/kWh)</th>
                <th>인증제 재생 인정(단순화)</th>
                <th>설명</th>
              </tr>
            </thead>
            <tbody>
              {data.sources.filter((s) => s.active).map((s) => {
                const f = data.factors.find((x) => x.id === s.efId);
                return (
                  <tr key={s.id}>
                    <td className="font-semibold">{s.label}</td>
                    <td className="text-caption text-ink-muted">{f?.name ?? s.efId}</td>
                  <td className="num">{f ? fmt(f.value, 4) : "없음"}</td>
                    <td>{s.certRenewable ? "예" : "아니오"}</td>
                    <td className="text-caption text-ink-muted">{s.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    case "technologies":
      return (
        <div className="table-scroll data-table-wrap">
          <table className={`data-table data-table-${kind}`}>
            <thead>
              <tr>
                <th>단계</th>
                <th>기술</th>
                <th>설명</th>
              </tr>
            </thead>
            <tbody>
              {data.technologies.filter((t) => t.active).map((t) => (
                <tr key={t.id}>
                  <td>{STAGE_LABEL[t.stage]}</td>
                  <td className="font-semibold">{t.name}</td>
                  <td className="text-caption text-ink-muted">{t.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "templates":
      return (
        <div className="table-scroll data-table-wrap">
          <table className={`data-table data-table-${kind}`}>
            <thead>
              <tr>
                <th>단계</th>
                <th>기술</th>
                <th>설비</th>
                <th>위치</th>
                <th>수소 흐름</th>
                <th>입력항목 (기본값, 단위)</th>
              </tr>
            </thead>
            <tbody>
              {data.templates.filter((t) => t.active).map((t) => (
                <tr key={t.id}>
                  <td>{STAGE_LABEL[t.stage]}</td>
                  <td className="text-caption text-ink-muted">{t.techId ? (data.technologies.find((x) => x.id === t.techId)?.name ?? t.techId) : "공통"}</td>
                  <td>
                    <span className="font-semibold">{t.name}</span>
                    {t.description && <span className="block text-footnote text-ink-subtle">{t.description}</span>}
                  </td>
                  <td>{LOCATION_LABEL[t.location]}</td>
                  <td className="text-caption">{t.h2.role === "producer" ? "생산" : t.h2.role === "passthrough" ? `통과, 손실 ${fmt(t.h2.defaultLossRate * 100, 1)}%` : "없음"}</td>
                  <td>
                    <ul className="flex flex-col gap-detail text-caption">
                      {t.inputs.map((i) => (
                        <li key={i.key}>
                          {i.label} <span className="font-mono text-ink-muted">{i.defaultAmount ?? "미입력"} {i.unit}{i.scaling === "perKgH2" ? "/kgH2" : ""}</span>
                          {i.help && <InfoTip label={`${i.label} 설명`}>{i.help}</InfoTip>}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "methods":
      return (
        <div className="grid grid-cols-1 gap-section lg:grid-cols-2">
          {data.methods.map((m) => (
            <section key={m.id} className="ws-section">
              <h2 className="text-head font-bold text-ink">{m.label}</h2>
              <p className="mt-label text-caption leading-relaxed text-ink-soft">{m.description}</p>
              <dl className="ws-kv mt-content">
                <dt>적용 표준</dt>
                <dd className="font-sans">{m.standards.join(", ")}</dd>
                <dt>GWP</dt>
                <dd className="font-sans">{m.gwp.basis}</dd>
                <dt>기능단위</dt>
                <dd className="font-sans">
                  수소 {m.functionalUnit.h2Kg} kg{m.functionalUnit.locked ? " (고정)" : " (사용자 설정)"}
                  {m.functionalUnit.purityMinPct !== undefined && `, 순도 ${m.functionalUnit.purityMinPct}% 이상`}
                </dd>
                <dt>시스템 경계</dt>
                <dd className="font-sans">{m.boundary.description}</dd>
                <dt>분모</dt>
                <dd className="font-sans">{DENOMINATOR_LABEL[m.denominator]}</dd>
                {m.grades && (
                  <>
                    <dt>등급</dt>
                    <dd className="font-sans">{m.grades.map((g) => `${g.grade}등급 이하 ${g.maxKgCO2eq}`).join(", ")} kgCO2eq/kgH2</dd>
                  </>
                )}
              </dl>
              <ul className="mt-content list-disc pl-section text-footnote leading-relaxed text-ink-subtle">
                {m.notices.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      );
    default:
      return (
        <div className="table-scroll data-table-wrap">
          <table className={`data-table data-table-${kind}`}>
            <thead>
              <tr>
                <th>용어</th>
                <th>정의</th>
                <th>출처</th>
              </tr>
            </thead>
            <tbody>
              {data.glossary.map((g) => (
                <tr key={g.key}>
                  <td>
                    <span className="font-semibold">{g.term}</span>
                    {g.termEn && <span className="block text-footnote text-ink-subtle">{g.termEn}</span>}
                  </td>
                  <td className="text-caption leading-relaxed">
                    {/* 기존 서버 용어에 남아 있는 상태 배지 안내도 표시하지 않는다. */}
                    {g.key === "emission_factor"
                      ? g.definition.replace(" 이 프로그램은 값 옆에 자료 상태(임시값,전문가 검토 완료,발주기관 확인 완료)를 붙인다.", "")
                      : g.definition}
                  </td>
                  <td className="text-footnote text-ink-muted">{g.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}
